import React, { useEffect, useState } from "react";
import { Book, Building2, FileText, Save, AlertTriangle, CheckCircle } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;
const DEMO_API_KEY = "test_key_123";
const DEMO_CLIENT_ID = "client_demo_123";

export const KnowledgeBasePage = () => {
  const [kb, setKb] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [saveMessage, setSaveMessage] = useState("");

  const apiKey = window.localStorage.getItem("apiKey") || DEMO_API_KEY;
  const clientId = window.localStorage.getItem("clientId") || DEMO_CLIENT_ID;

  useEffect(() => {
    const loadKb = async () => {
      try {
        const res = await fetch(`${API_URL}/api/knowledge-base/${clientId}`, {
          headers: { "X-API-Key": apiKey }
        });
        if (res.ok) {
          const data = await res.json();
          setKb(data);
        } else if (res.status === 404) {
          // If no knowledge base found, set empty object for new KB creation
          setKb({
            business_name: "",
            address: "",
            policies_text: ""
          });
        } else {
          setError("Unable to load knowledge base");
        }
      } catch (err) {
        console.error(err);
        setError(err.message || "Unable to load knowledge base");
      } finally {
        setLoading(false);
      }
    };
    loadKb();
  }, [apiKey, clientId]);

  const handleChange = (field, value) => {
    setKb((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSave = async () => {
    if (!apiKey || !kb) return;
    setSaving(true);
    setSaveMessage("");
    try {
      const res = await fetch(`${API_URL}/api/knowledge-base`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          "X-API-Key": apiKey
        },
        body: JSON.stringify({ knowledge_base: kb })
      });
      if (res.ok) {
        setSaveMessage("Saved successfully.");
      } else {
        setSaveMessage("Error saving knowledge base.");
      }
    } catch (err) {
      console.error(err);
      setSaveMessage(err.message || "Error saving knowledge base.");
    } finally {
      setSaving(false);
      setTimeout(() => setSaveMessage(""), 3000);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-slate-900">
            Knowledge Base
          </h1>
          <p className="text-slate-500 mt-1">
            The facts your AI agent relies on: services, hours, pricing, policies. This is the source of truth for CallBrain.
          </p>
        </div>
        <div className="flex items-center gap-2 px-4 py-2 bg-blue-50 border border-blue-200 rounded-full">
          <Book className="w-4 h-4 text-blue-600" />
          <span className="text-blue-700 text-sm font-medium">AI Knowledge</span>
        </div>
      </div>

      {error && (
        <div className="rounded-xl border border-red-200 bg-red-50 p-4 flex items-center gap-3">
          <AlertTriangle className="w-5 h-5 text-red-500 flex-shrink-0" />
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}

      {!error && !kb && (
        <div className="rounded-xl border border-slate-200 bg-gradient-to-br from-slate-50 to-white p-8 text-center">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-blue-100 flex items-center justify-center">
            <Book className="w-8 h-8 text-blue-500" />
          </div>
          <h3 className="text-lg font-semibold text-slate-900 mb-2">No knowledge base yet</h3>
          <p className="text-slate-500 text-sm max-w-md mx-auto">
            Your AI agent's knowledge base will be configured during onboarding.
          </p>
        </div>
      )}

      {kb && (
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="rounded-xl border border-slate-200 bg-white p-4">
              <h2 className="text-sm font-medium text-slate-900 mb-2">
                Business Info
              </h2>
              <label className="block mb-2 text-xs text-slate-700">
                Business Name
                <input
                  className="mt-1 w-full rounded-md bg-white border border-slate-300 px-2 py-1 text-xs text-slate-900"
                  value={kb.business_name || ""}
                  onChange={(e) =>
                    handleChange("business_name", e.target.value)
                  }
                />
              </label>
              <label className="block mb-2 text-xs text-slate-700">
                Address
                <textarea
                  className="mt-1 w-full rounded-md bg-white border border-slate-300 px-2 py-1 text-xs text-slate-900 min-h-[60px]"
                  value={kb.address || ""}
                  onChange={(e) => handleChange("address", e.target.value)}
                />
              </label>
            </div>

            <div className="rounded-xl border border-slate-200 bg-white p-4">
              <h2 className="text-sm font-medium text-slate-900 mb-2">
                Policies / Notes
              </h2>
              <textarea
                className="mt-1 w-full rounded-md bg-white border border-slate-300 px-2 py-1 text-xs text-slate-900 min-h-[120px]"
                value={kb.policies_text || ""}
                onChange={(e) =>
                  handleChange("policies_text", e.target.value)
                }
                placeholder="Cancellation policy, late arrivals, special instructions..."
              />
            </div>
          </div>

          <div className="rounded-xl border border-slate-200 bg-white p-4">
            <h2 className="text-sm font-medium text-slate-900 mb-2">
              Raw JSON (for power users)
            </h2>
            <p className="text-[11px] text-slate-600 mb-2">
              This is the full knowledge base object your AI uses. You can tweak
              it directly for now. Later, this will become a structured editor.
            </p>
            <textarea
              className="mt-1 w-full rounded-md bg-slate-50 border border-slate-300 px-2 py-1 text-[11px] text-slate-900 min-h-[260px] font-mono"
              value={JSON.stringify(kb, null, 2)}
              onChange={(e) => {
                try {
                  const parsed = JSON.parse(e.target.value);
                  setKb(parsed);
                  setError(null);
                } catch {
                  setError("Invalid JSON syntax. Fix before saving.");
                }
              }}
            />
          </div>

          <div className="flex items-center gap-3">
            <button
              onClick={handleSave}
              disabled={saving || !!error}
              className="inline-flex items-center px-3 py-1.5 rounded-md bg-sky-500 hover:bg-sky-400 disabled:bg-slate-700 text-xs font-medium text-white"
            >
              {saving ? "Saving..." : "Save Knowledge Base"}
            </button>
            {saveMessage && (
              <span className="text-[11px] text-slate-700">
                {saveMessage}
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
